// TMOS Tenant Positions Page
// Purpose: Tenant admins manage their tenant's position list (create, edit, delete)

import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { businessPositionService, BusinessPosition } from '@/services/businessPositionService';
import { toast } from 'react-hot-toast';
import { PlusIcon, PencilIcon, TrashIcon, BriefcaseIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';

const CATEGORIES = ['EXECUTIVE', 'LEADERSHIP', 'MANAGEMENT', 'OPERATIONAL', 'PROFESSIONAL_SERVICES'] as const;

export const TenantPositionsPage: React.FC = () => {
  const { t } = useLanguage();
  const [positions, setPositions] = useState<BusinessPosition[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({
    position_code: '',
    position_name: '',
    position_category: '' as string,
    description: '',
    is_active: true
  });
  const [saving, setSaving] = useState(false);

  const loadPositions = async () => {
    try {
      setLoading(true);
      const list = await businessPositionService.getActivePositions();
      setPositions(list);
    } catch (err: unknown) {
      const e = err as { message?: string };
      toast.error(e?.message || 'Failed to load positions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPositions();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm({
      position_code: '',
      position_name: '',
      position_category: '',
      description: '',
      is_active: true
    });
    setModalOpen(true);
  };

  const openEdit = (p: BusinessPosition) => {
    setEditingId(p.id);
    setForm({
      position_code: p.position_code,
      position_name: p.position_name,
      position_category: p.position_category || '',
      description: p.description || '',
      is_active: p.is_active !== false
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.position_code?.trim() || !form.position_name?.trim()) {
      toast.error(t('tenantPositions.codeAndNameRequired') || 'Position code and name are required');
      return;
    }
    try {
      setSaving(true);
      if (editingId) {
        await businessPositionService.updateTenantPosition(editingId, {
          position_name: form.position_name.trim(),
          position_category: form.position_category || undefined,
          description: form.description?.trim() || undefined,
          is_active: form.is_active
        });
        toast.success(t('tenantPositions.updated') || 'Position updated');
      } else {
        await businessPositionService.createTenantPosition({
          position_code: form.position_code.trim().toUpperCase(),
          position_name: form.position_name.trim(),
          position_category: form.position_category || undefined,
          description: form.description?.trim() || undefined,
          is_active: form.is_active
        });
        toast.success(t('tenantPositions.created') || 'Position created');
      }
      setModalOpen(false);
      loadPositions();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t('tenantPositions.confirmDelete') || 'Delete this position?')) return;
    try {
      await businessPositionService.deleteTenantPosition(id);
      toast.success(t('tenantPositions.deleted') || 'Position deleted');
      loadPositions();
    } catch (err: unknown) {
      const e = err as { message?: string };
      toast.error(e?.message || 'Failed to delete');
    }
  };

  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)]">
      <div className="container mx-auto px-4 py-6">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[var(--color-surface-text)] mb-2">
              {t('tenantPositions.title') || 'Positions'}
            </h1>
            <p className="text-[var(--color-secondary-text)]">
              {t('tenantPositions.description') || 'Manage your organization\'s positions. Users can select a primary position in their profile.'}
            </p>
          </div>
          <Button onClick={openCreate} leftIcon={<PlusIcon className="w-5 h-5" />}>
            {t('tenantPositions.addPosition') || 'Add position'}
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BriefcaseIcon className="w-5 h-5" />
              {t('tenantPositions.list') || 'Position list'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-[var(--color-secondary-text)]">{t('common.loading') || 'Loading...'}</p>
            ) : positions.length === 0 ? (
              <p className="text-[var(--color-secondary-text)]">{t('tenantPositions.noPositions') || 'No positions yet. Add one or ask your platform admin to provision templates.'}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-[var(--color-border)]">
                      <th className="py-2 pr-4 font-medium text-[var(--color-surface-text)]">Code</th>
                      <th className="py-2 pr-4 font-medium text-[var(--color-surface-text)]">Name</th>
                      <th className="py-2 pr-4 font-medium text-[var(--color-surface-text)]">Category</th>
                      <th className="py-2 pr-4 font-medium text-[var(--color-surface-text)]">Status</th>
                      <th className="py-2 font-medium text-[var(--color-surface-text)]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {positions.map((p) => (
                      <tr key={p.id} className="border-b border-[var(--color-border)]">
                        <td className="py-2 pr-4 font-mono text-sm">{p.position_code}</td>
                        <td className="py-2 pr-4">{p.position_name}</td>
                        <td className="py-2 pr-4">{p.position_category || '—'}</td>
                        <td className="py-2 pr-4">
                          <Badge variant={p.is_active !== false ? 'success' : 'default'}>
                            {p.is_active !== false ? (t('common.active') || 'Active') : (t('common.inactive') || 'Inactive')}
                          </Badge>
                        </td>
                        <td className="py-2 flex gap-2">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(p)} leftIcon={<PencilIcon className="w-4 h-4" />}>
                            {t('common.edit') || 'Edit'}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(p.id)} leftIcon={<TrashIcon className="w-4 h-4" />}>
                            {t('common.delete') || 'Delete'}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => !saving && setModalOpen(false)}>
          <div className="bg-[var(--color-surface)] rounded-lg shadow-xl max-w-md w-full mx-4 p-6" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-semibold mb-4">
              {editingId ? (t('tenantPositions.editPosition') || 'Edit position') : (t('tenantPositions.newPosition') || 'New position')}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t('tenantPositions.code') || 'Code'}</label>
                <input
                  className="w-full border border-[var(--color-border)] rounded px-3 py-2 bg-[var(--color-secondary-bg)]"
                  value={form.position_code}
                  onChange={e => setForm(f => ({ ...f, position_code: e.target.value }))}
                  placeholder="e.g. VP_ENG"
                  disabled={!!editingId}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('tenantPositions.name') || 'Name'}</label>
                <input
                  className="w-full border border-[var(--color-border)] rounded px-3 py-2 bg-[var(--color-secondary-bg)]"
                  value={form.position_name}
                  onChange={e => setForm(f => ({ ...f, position_name: e.target.value }))}
                  placeholder="e.g. VP Engineering"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('tenantPositions.category') || 'Category'}</label>
                <select
                  className="w-full border border-[var(--color-border)] rounded px-3 py-2 bg-[var(--color-secondary-bg)]"
                  value={form.position_category}
                  onChange={e => setForm(f => ({ ...f, position_category: e.target.value }))}
                >
                  <option value="">—</option>
                  {CATEGORIES.map(c => (
                    <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('tenantPositions.description') || 'Description'}</label>
                <textarea
                  className="w-full border border-[var(--color-border)] rounded px-3 py-2 bg-[var(--color-secondary-bg)]"
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  rows={2}
                />
              </div>
              {editingId && (
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
                  <span>{t('common.active') || 'Active'}</span>
                </label>
              )}
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setModalOpen(false)} disabled={saving}>{t('common.cancel') || 'Cancel'}</Button>
              <Button onClick={handleSave} disabled={saving}>{saving ? (t('common.saving') || 'Saving...') : (t('common.save') || 'Save')}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
